import { useCallback } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import PropTypes from 'prop-types';

import ConfirmModal from '#client/components/ConfirmModal';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useToastContext from '#client/hooks/useToastContext';
import { CANCEL_PAYMENT_MUTATION } from '#client/mutations/payment';

export default function ConfirmCancelTransactionModal({ id, handleSuccess, handleClose }) {
  const { createSuccessToast, createErrorToast } = useToastContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();

  const [cancelPayment, { loading }] = useMutation(CANCEL_PAYMENT_MUTATION, {
    noThrow: true
  });

  const trackCancelTransactionClick = useButtonPressedCallback({
    elementText: 'Yes, cancel',
    elementName: 'yes_cancel',
    appPageName: 'banking_cancel_payment_confirmation'
  });

  /**
   * handle cancel transaction
   */
  const handleCancelTransaction = useCallback(async () => {
    /**
     * Tracking Details: Tracking added to confirm cancel transaction
     * Question: How many user click confirm cancel transaction button?
     */
    trackCancelTransactionClick();

    const { error: cancelError } = await cancelPayment({
      variables: {
        input: {
          businessEntityUuid,
          id
        }
      },
      skip: loading
    });

    if (cancelError) {
      // Show toast error
      createErrorToast({
        title: 'We were unable to cancel your transaction',
        description: 'Please reach out to support for additional help',
        error: cancelError
      });
    } else {
      createSuccessToast({
        title: 'Your transaction has been canceled'
      });
      handleSuccess();
    }
  }, [
    businessEntityUuid,
    cancelPayment,
    createErrorToast,
    createSuccessToast,
    handleSuccess,
    id,
    loading,
    trackCancelTransactionClick
  ]);

  const trackCloseCancelTransactionClick = useButtonPressedCallback({
    elementText: 'No, don’t cancel',
    elementName: 'no_dont_cancel',
    appPageName: 'banking_cancel_payment_confirmation'
  });

  const handleCloseCancelTransactionModal = () => {
    /**
     * Tracking Details: Tracking added to close confirm cancel transaction modal
     * Question: How many user click close confirm cancel transaction modal button?
     */
    trackCloseCancelTransactionClick();
  };

  return (
    <ConfirmModal
      cancelText="No, don’t cancel"
      handleCancel={handleCloseCancelTransactionModal}
      handleClose={handleClose}
      handleOK={handleCancelTransaction}
      loading={loading}
      message="You can’t undo this action."
      okText="Yes, cancel"
      title="Are you sure you want to cancel this transaction?"
    />
  );
}

ConfirmCancelTransactionModal.propTypes = {
  id: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSuccess: PropTypes.func.isRequired
};
